import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { relativeTime } from '../../lib/utils';
const STATUS_ORDER = {
    running: 0,
    assigned: 1,
    pending: 2,
    blocked: 3,
    failed: 4,
    completed: 5,
    cancelled: 6,
};
function statusBadge(status) {
    switch (status.toLowerCase()) {
        case 'running':
            return { bg: 'bg-blue-100', text: 'text-blue-800' };
        case 'completed':
            return { bg: 'bg-green-100', text: 'text-green-800' };
        case 'pending':
        case 'assigned':
            return { bg: 'bg-gray-100', text: 'text-gray-800' };
        case 'failed':
            return { bg: 'bg-red-100', text: 'text-red-800' };
        case 'blocked':
            return { bg: 'bg-yellow-100', text: 'text-yellow-800' };
        case 'cancelled':
            return { bg: 'bg-slate-100', text: 'text-slate-800' };
        default:
            return { bg: 'bg-gray-100', text: 'text-gray-800' };
    }
}
function agentDotColor(agentType) {
    switch (agentType.toLowerCase()) {
        case 'coder':
            return 'bg-green-500';
        case 'tester':
            return 'bg-purple-500';
        case 'reviewer':
            return 'bg-blue-500';
        case 'deployer':
            return 'bg-orange-500';
        case 'planner':
            return 'bg-gray-500';
        case 'analyzer':
            return 'bg-cyan-500';
        case 'ui_designer':
            return 'bg-pink-500';
        default:
            return 'bg-gray-400';
    }
}
function SortArrow({ active, direction }) {
    if (!active)
        return null;
    return (_jsx("svg", { className: "w-3 h-3 ml-1 inline", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: direction === 'asc' ? (_jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 15l7-7 7 7" })) : (_jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 9l-7 7-7-7" })) }));
}
function RetrySpinner() {
    return (_jsxs("svg", { className: "animate-spin h-4 w-4 text-gray-500", fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" })] }));
}
export function TaskTable({ tasks, onRetry }) {
    const [sortKey, setSortKey] = useState('status');
    const [sortDir, setSortDir] = useState('asc');
    const [retryingId, setRetryingId] = useState(null);
    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        }
        else {
            setSortKey(key);
            setSortDir('asc');
        }
    };
    const sorted = useMemo(() => {
        const list = [...tasks];
        list.sort((a, b) => {
            let cmp = 0;
            switch (sortKey) {
                case 'status':
                    cmp = (STATUS_ORDER[a.status.toLowerCase()] ?? 99) - (STATUS_ORDER[b.status.toLowerCase()] ?? 99);
                    break;
                case 'title':
                    cmp = a.title.localeCompare(b.title);
                    break;
                case 'agentType':
                    cmp = a.agentType.localeCompare(b.agentType);
                    break;
                case 'priority':
                    cmp = a.priority - b.priority;
                    break;
                case 'createdAt':
                    cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                    break;
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [tasks, sortKey, sortDir]);
    const handleRetry = async (taskId) => {
        setRetryingId(taskId);
        try {
            await onRetry(taskId);
        }
        finally {
            setRetryingId(null);
        }
    };
    const headerClass = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700';
    if (tasks.length === 0) {
        return (_jsx("div", { className: "bg-white rounded-lg border border-gray-200 p-8 text-center", children: _jsx("p", { className: "text-gray-400", children: "No tasks yet" }) }));
    }
    return (_jsx("div", { className: "bg-white rounded-lg border border-gray-200 overflow-x-auto", children: _jsxs("table", { className: "min-w-full divide-y divide-gray-200", children: [_jsx("thead", { className: "bg-gray-50", children: _jsxs("tr", { children: [_jsxs("th", { className: headerClass, onClick: () => handleSort('status'), children: ["Status", _jsx(SortArrow, { active: sortKey === 'status', direction: sortDir })] }), _jsxs("th", { className: headerClass, onClick: () => handleSort('title'), children: ["Title", _jsx(SortArrow, { active: sortKey === 'title', direction: sortDir })] }), _jsxs("th", { className: headerClass, onClick: () => handleSort('agentType'), children: ["Agent", _jsx(SortArrow, { active: sortKey === 'agentType', direction: sortDir })] }), _jsxs("th", { className: headerClass, onClick: () => handleSort('priority'), children: ["Priority", _jsx(SortArrow, { active: sortKey === 'priority', direction: sortDir })] }), _jsxs("th", { className: headerClass, onClick: () => handleSort('createdAt'), children: ["Created", _jsx(SortArrow, { active: sortKey === 'createdAt', direction: sortDir })] }), _jsx("th", { className: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "Actions" })] }) }), _jsx("tbody", { className: "bg-white divide-y divide-gray-200", children: sorted.map((task) => {
                        const { bg, text } = statusBadge(task.status);
                        const dotColor = agentDotColor(task.agentType);
                        const canRetry = task.status.toLowerCase() === 'failed' || task.status.toLowerCase() === 'blocked';
                        return (_jsxs("tr", { className: "hover:bg-gray-50", children: [_jsx("td", { className: "px-4 py-3 whitespace-nowrap", children: _jsx("span", { className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`, children: task.status }) }), _jsx("td", { className: "px-4 py-3", children: _jsx("span", { className: "text-sm text-gray-900 truncate block max-w-xs", title: task.title, children: task.title }) }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: `inline-block w-2 h-2 rounded-full ${dotColor}` }), _jsx("span", { className: "text-sm text-gray-700", children: task.agentType })] }) }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap text-sm text-gray-700", children: task.priority }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap text-sm text-gray-500", children: relativeTime(task.createdAt) }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap", children: canRetry && (_jsx("button", { onClick: () => handleRetry(task.id), disabled: retryingId === task.id, className: "p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed", title: "Retry task", children: retryingId === task.id ? (_jsx(RetrySpinner, {})) : (_jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" }) })) })) })] }, task.id));
                    }) })] }) }));
}
